import { COMPARISON_COLORS } from 'features/labeler/comparison'
import { useLabelerOptional } from 'features/labeler/LabelerContext'
import {
  colorForBox,
  predictionColor,
  SELECTED_COLOR,
} from 'features/labeler/taxonomy'
import type { NormalizedBox } from 'features/labeler/types'
import React from 'react'

type LabelerOverlayProps = {
  imageNatural: { w: number; h: number }
}

function rectFor(bbox: NormalizedBox, imageNatural: { w: number; h: number }) {
  return {
    x: bbox.x_min * imageNatural.w,
    y: bbox.y_min * imageNatural.h,
    width: (bbox.x_max - bbox.x_min) * imageNatural.w,
    height: (bbox.y_max - bbox.y_min) * imageNatural.h,
  }
}

// vector-effect keeps strokes at constant SCREEN width across zoom — a
// fixed sub-pixel border disappears at full-sheet view, an image-space one
// turns into a slab when framed on a single instrument bubble.
const STROKE_PROPS = {
  rx: 2,
  ry: 2,
  strokeWidth: 1.5,
  vectorEffect: 'non-scaling-stroke',
} as const

/**
 * Labeling-mode boxes over the page image. Before a page is seeded the raw
 * predictions render as read-only ghosts; after seeding, the working copy is
 * the single source of truth.
 */
/** Hovering raises the fill alpha while the border stays put. */
function hoverFill(fill: string, selected: boolean): string {
  return fill.replace(/[\d.]+\)$/, selected ? '0.25)' : '0.3)')
}

export function LabelerOverlay({ imageNatural }: LabelerOverlayProps) {
  const labeler = useLabelerOptional()
  const [hoveredId, setHoveredId] = React.useState<string | null>(null)
  if (!labeler?.active || !imageNatural.w || !imageNatural.h) return null

  const {
    page,
    isSeeded,
    predictions,
    taxonomy,
    visibleBoxes,
    selectedBoxId,
    selectedBoxIds,
    selectBox,
    liveBbox,
    gestureDraft,
    isDrawingMode,
    compareMode,
    comparison,
    compareCategoryFilter,
    selectedComparisonId,
    selectComparisonItem,
  } = labeler

  if (compareMode && comparison) {
    const items = comparison.items.filter(
      (i) => !compareCategoryFilter || i.category === compareCategoryFilter
    )
    const ordered = [
      ...items.filter((i) => i.category === 'detection_miss'),
      ...items.filter((i) => i.category !== 'detection_miss'),
    ]
    return (
      <g data-labeler-overlay='comparison'>
        {ordered.map((item) => {
          const paint = COMPARISON_COLORS[item.category]
          const isSelected = item.id === selectedComparisonId
          const isHovered = item.id === hoveredId
          const rect = rectFor(item.bbox, imageNatural)
          const baseFill = isSelected ? 'rgba(6, 182, 212, 0.2)' : paint.fill
          return (
            <rect
              key={item.id}
              {...rect}
              {...STROKE_PROPS}
              strokeWidth={isSelected ? 3 : 1.5}
              fill={
                isHovered
                  ? isSelected
                    ? 'rgba(6, 182, 212, 0.3)'
                    : hoverFill(paint.fill, false)
                  : baseFill
              }
              stroke={isSelected ? 'rgba(6, 182, 212, 0.9)' : paint.stroke}
              strokeDasharray={paint.dashed ? '6,4' : undefined}
              style={{
                cursor: 'pointer',
                pointerEvents: 'auto',
                transition: 'fill 0.1s',
              }}
              onMouseEnter={() => setHoveredId(item.id)}
              onMouseLeave={() =>
                setHoveredId((cur) => (cur === item.id ? null : cur))
              }
              onClick={(event) => {
                event.stopPropagation()
                selectComparisonItem(item.id, { zoom: false })
              }}>
              <title>{`${paint.label}${item.iou !== null ? ` · IoU ${item.iou.toFixed(3)}` : ''}${item.predictedText ? ` · Pred: ${item.predictedText}` : ''}${item.gtText ? ` · GT: ${item.gtText}` : ''}`}</title>
            </rect>
          )
        })}
      </g>
    )
  }

  const showGhosts = !page?.seedRunId && !!predictions && predictions.length > 0
  const ghosts = showGhosts ? (
    <g data-labeler-overlay-ghosts='true'>
      {predictions.map((p) => {
        const rect = rectFor(
          {
            x_min: Math.min(p.x0, p.x1),
            y_min: Math.min(p.y0, p.y1),
            x_max: Math.max(p.x0, p.x1),
            y_max: Math.max(p.y0, p.y1),
          },
          imageNatural
        )
        const paint = predictionColor(taxonomy, p.type)
        return (
          <rect
            key={p.id}
            {...rect}
            {...STROKE_PROPS}
            fill={paint.fill}
            stroke={paint.stroke}
            strokeDasharray='6,3'
            style={{ pointerEvents: 'none' }}>
            <title>{`${p.text ?? ''} · ${p.type ?? 'untyped'}`}</title>
          </rect>
        )
      })}
    </g>
  ) : null

  if (!isSeeded) {
    return ghosts && <g data-labeler-overlay='predictions'>{ghosts}</g>
  }

  const visibleIds = new Set(visibleBoxes.map((b) => b.id))
  const boxes = (page?.boxes ?? []).filter((b) => visibleIds.has(b.id))
  const selectedBox = boxes.find((b) => b.id === selectedBoxId)

  const ordered = [
    ...boxes.filter((b) => b.id !== selectedBoxId && !selectedBoxIds.has(b.id)),
    ...boxes.filter((b) => selectedBoxIds.has(b.id) && b.id !== selectedBoxId),
    ...boxes.filter((b) => b.id === selectedBoxId),
  ]

  return (
    <g data-labeler-overlay='working'>
      {ghosts}
      {ordered.map((box) => {
        const isSelected = box.id === selectedBoxId
        const isHovered = box.id === hoveredId
        const bbox =
          liveBbox && liveBbox.boxId === box.id ? liveBbox.bbox : box.bbox
        const rect = rectFor(bbox, imageNatural)
        const paint = colorForBox(box, {
          selected: isSelected,
          bulkSelected: selectedBoxIds.has(box.id),
        })
        return (
          <rect
            key={box.id}
            {...rect}
            {...STROKE_PROPS}
            strokeWidth={isSelected ? 3 : 1.5}
            fill={isHovered ? hoverFill(paint.fill, isSelected) : paint.fill}
            stroke={paint.stroke}
            strokeDasharray={paint.dashed ? '5,3' : undefined}
            style={{
              cursor: isSelected && !isDrawingMode ? 'move' : 'pointer',
              pointerEvents: 'auto',
              transition: 'fill 0.1s',
            }}
            onMouseEnter={() => setHoveredId(box.id)}
            onMouseLeave={() =>
              setHoveredId((cur) => (cur === box.id ? null : cur))
            }
            onClick={(event) => {
              event.stopPropagation()
              if (event.shiftKey) {
                labeler.setSelectedBoxIds((cur) => {
                  const next = new Set(cur)
                  if (next.has(box.id)) next.delete(box.id)
                  else next.add(box.id)
                  return next
                })
              } else {
                selectBox(box.id, { zoom: false })
              }
            }}>
            <title>{`${box.text || '(no text)'} · ${box.type ?? 'untyped'}${box.verified ? ' ✓' : ''}`}</title>
          </rect>
        )
      })}

      {gestureDraft && (
        <rect
          x={gestureDraft.rect.x}
          y={gestureDraft.rect.y}
          width={gestureDraft.rect.width}
          height={gestureDraft.rect.height}
          fill={
            gestureDraft.kind === 'draw'
              ? 'rgba(250, 204, 21, 0.3)'
              : 'rgba(59, 130, 246, 0.15)'
          }
          stroke={
            gestureDraft.kind === 'draw' ? '#facc15' : 'rgba(59, 130, 246, 0.8)'
          }
          strokeWidth={1}
          strokeDasharray='4,3'
          vectorEffect='non-scaling-stroke'
          style={{ pointerEvents: 'none' }}
        />
      )}

      {/* Resize affordances: cursor-only edge strips on the selected box —
          the gesture itself starts from the SVG-level capture listener. */}
      {selectedBox &&
        !isDrawingMode &&
        (() => {
          const bbox =
            liveBbox && liveBbox.boxId === selectedBox.id
              ? liveBbox.bbox
              : selectedBox.bbox
          const r = rectFor(bbox, imageNatural)
          const t = Math.max(4, Math.min(r.width, r.height) * 0.15)
          const strip = {
            fill: 'transparent',
            stroke: 'none',
          }
          return (
            <g data-labeler-resize-handles='true'>
              <rect
                {...strip}
                x={r.x}
                y={r.y - t / 2}
                width={r.width}
                height={t}
                style={{ cursor: 'ns-resize', pointerEvents: 'auto' }}
              />
              <rect
                {...strip}
                x={r.x}
                y={r.y + r.height - t / 2}
                width={r.width}
                height={t}
                style={{ cursor: 'ns-resize', pointerEvents: 'auto' }}
              />
              <rect
                {...strip}
                x={r.x - t / 2}
                y={r.y}
                width={t}
                height={r.height}
                style={{ cursor: 'ew-resize', pointerEvents: 'auto' }}
              />
              <rect
                {...strip}
                x={r.x + r.width - t / 2}
                y={r.y}
                width={t}
                height={r.height}
                style={{ cursor: 'ew-resize', pointerEvents: 'auto' }}
              />
              <rect
                x={r.x}
                y={r.y}
                width={r.width}
                height={r.height}
                fill='none'
                stroke={SELECTED_COLOR.stroke}
                strokeWidth={1}
                strokeDasharray='2,2'
                vectorEffect='non-scaling-stroke'
                style={{ pointerEvents: 'none' }}
              />
            </g>
          )
        })()}
    </g>
  )
}
